import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SalaryContext = createContext({ status: null, visible: false, refresh: async () => null, invalidate: () => {} });

/** One status owner for the authenticated user/company; the server is always authoritative. */
export const SalaryVisibilityProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const snapshot = useRef(null), request = useRef(null), version = useRef(0);
  const invalidate = useCallback(() => {
    version.current++; request.current?.controller.abort(); request.current = null;
    snapshot.current = snapshot.current ? { ...snapshot.current, ontgrendeld: false, tot: null } : null;
    setStatus(snapshot.current);
  }, []);
  const refresh = useCallback(() => {
    if (request.current) return request.current.promise;
    const epoch = ++version.current, current = { controller: new AbortController(), promise: null };
    request.current = current;
    current.promise = (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/loon-status`, { signal: current.controller.signal, timeout: 12000 });
        if (epoch !== version.current) return null;
        snapshot.current = data; setStatus(data); return data;
      } catch (e) {
        if (epoch === version.current && !axios.isCancel(e)) invalidate();
        return null;
      } finally { if (request.current === current) request.current = null; }
    })();
    return current.promise;
  }, [invalidate]);

  useEffect(() => {
    let active = true, timer;
    const tick = async () => {
      if (document.visibilityState === "visible") await refresh();
      if (active) timer = setTimeout(tick, !snapshot.current || (snapshot.current.vereist && snapshot.current.ontgrendeld) ? 5000 : 30000);
    };
    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      if (snapshot.current?.vereist && snapshot.current.tot && Date.parse(snapshot.current.tot) <= Date.now()) invalidate();
      void refresh();
    };
    const interceptor = axios.interceptors.response.use((response) => {
      const path = response.config.url?.split("?")[0] || "";
      if (path.endsWith("/auth/loon-vergrendel") || path.endsWith("/auth/logout")) invalidate();
      if (path.endsWith("/auth/loon-ontgrendel")) void refresh();
      return response;
    }, async (error) => {
      const code = error.response?.status;
      let detail = error.response?.data?.detail;
      if (code === 403 && typeof detail !== "string") {
        // PDF requests receive FastAPI's JSON denial as a Blob. Keep the original
        // error intact for the download handler, but inspect small error bodies.
        let raw = error.response?.data;
        try {
          if (raw instanceof Blob && raw.size <= 65536) raw = await raw.text();
          else if (raw instanceof ArrayBuffer && raw.byteLength <= 65536) raw = new TextDecoder().decode(raw);
          if (typeof raw === "string" && raw.length <= 65536) detail = JSON.parse(raw)?.detail;
        } catch { /* An unrelated/non-JSON denial must not grant visibility. */ }
      }
      if (code === 401 || (code === 403 && typeof detail === "string" && /loongegevens|loontoegang|vergrendeld/i.test(detail))) invalidate();
      return Promise.reject(error);
    });
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    void tick();
    return () => {
      active = false; clearTimeout(timer); version.current++;
      request.current?.controller.abort(); request.current = null;
      axios.interceptors.response.eject(interceptor);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh, invalidate]);

  useEffect(() => {
    if (!status?.ontgrendeld || !status.vereist || !status.tot) return;
    const delay = Date.parse(status.tot) - Date.now();
    if (!Number.isFinite(delay) || delay <= 0) { invalidate(); return; }
    const timer = setTimeout(invalidate, Math.min(delay, 2147483647));
    return () => clearTimeout(timer);
  }, [status?.ontgrendeld, status?.vereist, status?.tot, invalidate]);

  const visible = status?.ontgrendeld === true && (!status.vereist || (status.tot && Date.parse(status.tot) > Date.now()));
  return <SalaryContext.Provider value={{ status, visible: Boolean(visible), refresh, invalidate }}>{children}</SalaryContext.Provider>;
};
export const useSalaryStatus = () => useContext(SalaryContext);